import { z } from 'zod'

export const collectionMetadataSchema = z
  .object({
    total: z.number().int().nonnegative(),
    page: z
      .number()
      .int()
      .positive()
      .nullish()
      .transform((value) => value ?? 1),
    per_page: z
      .number()
      .int()
      .positive()
      .nullish()
      .transform((value) => value ?? 25),
    next: z
      .string()
      .nullish()
      .transform((value) => value ?? null),
    previous: z
      .string()
      .nullish()
      .transform((value) => value ?? null),
  })
  .refine(({ total, page, per_page }) => total === 0 || (page - 1) * per_page < total, {
    path: ['page'],
    message: 'De pagina valt buiten de collectie.',
  })

export function uuidCollectionSchema<TModelSchema extends z.ZodType>(modelSchema: TModelSchema) {
  return z.object({
    items: z.record(z.uuid(), modelSchema),
    metadata: collectionMetadataSchema.nullish(),
  })
}

export function collectionRequestSchema<TModelFilterSchema extends z.ZodType>(
  filterSchema: TModelFilterSchema,
) {
  return z.object({
    filter: filterSchema.optional(),
    page: z.number().int().positive().nullish().default(1),
    per_page: z.number().int().positive().max(250).nullish().default(25),
  })
}

export const apiViolationSchema = z.object({
  propertyPath: z.string(),
  code: z.string(),
  message: z.string(),
})

export const apiProblemSchema = z.object({
  type: z.string().optional(),
  title: z.string(),
  status: z.number().int().min(400).max(599),
  code: z.string().optional(),
  detail: z.string().optional(),

  violations: z
    .array(apiViolationSchema)
    .nullish()
    .transform((value) => value ?? []),
})

export type ApiProblem = z.infer<typeof apiProblemSchema>

export const optionalBoolSchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined ||
      (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Unavailable due to too many joins, reduce your selection bij providing fields with a maximum of 61.'))) {
      return null
    }

    return String(value)
  },
  z.stringbool().nullable().optional(),
)
export const optionalDateTimeSchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined ||
      (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Unavailable due to too many joins, reduce your selection bij providing fields with a maximum of 61.'))) {
      return null
    }

    return String(value)
  },
  z.iso.datetime().nullable().optional(),
)
export const optionalDateSchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined ||
      (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Unavailable due to too many joins, reduce your selection bij providing fields with a maximum of 61.'))) {
      return null
    }

    return String(value)
  },
  z.iso.date().nullable().optional(),
)
export const optionalStringSchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined ||
      (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Unavailable due to too many joins, reduce your selection bij providing fields with a maximum of 61.'))) {
      return null
    }

    return String(value)
  },
  z.string().nullable().optional(),
)
export const optionalNumberSchema = z.preprocess(
  (value) => {
    if (typeof value === 'number') {
      return value;
    }

    if (
      typeof value === 'string' &&
      /^\d+(?:\.\d+)?$/.test(value.trim())
    ) {
      return Number(value)
    }

    if (value === null || value === undefined ||
      (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Unavailable due to too many joins, reduce your selection bij providing fields with a maximum of 61.'))) {
      return null
    }

    return value
  },
  z.number().nullable().optional(),
)
export const optionalNumberArraySchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined ||
      (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Unavailable due to too many joins, reduce your selection bij providing fields with a maximum of 61.'))) {
      return null
    }

    if (typeof value === 'object' && Array.isArray(value)) {
      return value;
    }

    return [value]
  },
  z.array(optionalNumberSchema).nullable().optional(),
)
export const optionalNumberOrStringSchema = z.preprocess(
  (value) => {
    if (typeof value === 'number') {
      return value;
    }

    if (
      typeof value === 'string' &&
      /^\d+(?:\.\d+)?$/.test(value.trim())
    ) {
      return Number(value)
    }

    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && (value.trim() === '' || value.trim() === 'Unavailable due to too many joins, reduce your selection bij providing fields with a maximum of 61.'))
    ) {
      return null
    }

    return value
  },
  z.union([
    z.number(),
    z.string(),
  ]).nullable().optional(),
)
